import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { generatePath, Link } from 'react-router-dom';

import { CustomerOrderDto, OrderAction, OrderType, PriceType } from 'common/api/autogenerated/trading-gateway';

import { AssetIcons, NumbersConcealer, SvgIconUrl, Tooltip } from 'common/components';
import { RangeCompletedIndicator } from 'common/components/RangeCompletedIndicator/RangeCompletedIndicator';
import { TooltipTriggerType } from 'common/components/Tooltip/Tooltip.component';

import { calculateOrderCost } from 'common/helpers';
import { localizeNumber } from 'common/helpers/balance.helpers';
import { useBowser, useCustomer, useI18n, useSpotOrders, useStream } from 'common/hooks';
import { useWallet } from 'common/hooks/useWallet/useWallet.hook';
import svgIcons from 'common/svgIcons';

import { Dropdown, DropdownOption } from 'components/Dropdown/Dropdown.component';

import { routes } from 'routes/routes.constants';

import { capitalizeFirstLetter } from 'utils/capitalizeFirstLetter';
import { formatDate } from 'utils/date';
import { detectPrecision } from 'utils/detectPrecision';

type Props = CustomerOrderDto & {
  isColumn: boolean;
  isRow: boolean;
  isTest?: boolean;
  onCancel?: (id: number) => void;
  disableRedirect?: boolean;
};

type DropdownActions = 'edit' | 'delete';

export const Order: React.FC<React.PropsWithChildren<Props>> = memo((props) => {
  const { translate } = useI18n();
  const { isMobile } = useBowser();
  const { checkFeaturesAvailability } = useCustomer();
  const { onEditOrder, onConfirmCancelOrder } = useSpotOrders();

  const { isColumn, isRow, isTest, disableRedirect } = useMemo(() => props, [props]);
  const { onCancel, ...order } = useMemo(() => props, [props]);
  const {
    action,
    openTime,
    id,
    symbol,
    type,
    amount = 0,
    initialAmount = 0,
    price,
    baseCurrency,
    quoteCurrency,
    makerCommission = 0,
  } = useMemo(() => order, [order]);

  const orderTypeText = useMemo(
    () =>
      translate.toText(type === OrderType.MARKET ? 'mobile:open-order:type:market' : 'mobile:open-order:type:limit'),
    [type, translate],
  );

  const onDropdownSelect = useCallback(
    (option: DropdownOption<DropdownActions>) => {
      if (option.value === 'edit') onEditOrder(id || 0, action);
      else onConfirmCancelOrder(order);
    },
    [onEditOrder, id, action, onConfirmCancelOrder, order],
  );

  const {
    subscribe,
    stream: { bid = 0, prevBid = 0, ask = 0, prevAsk = 0, digits },
  } = useStream(String(symbol));

  const priceType = useMemo(() => (action === OrderAction.BUY ? PriceType.BEST_ASK : PriceType.BEST_BID), [action]);

  const isBuy = useMemo(() => action === OrderAction.BUY, [action]);

  useEffect(() => {
    if (isTest || !symbol) return;

    const { unsubscribe } = subscribe(symbol, priceType);

    return () => unsubscribe();
  }, [isTest, priceType, subscribe, symbol]);

  const value = useMemo(() => (action === OrderAction.BUY ? ask : bid), [action, ask, bid]);
  const prevValue = useMemo(() => (action === OrderAction.BUY ? prevAsk : prevBid), [action, prevAsk, prevBid]);

  const isOrderPartiallyCompleted = useMemo(() => amount < initialAmount, [amount, initialAmount]);

  const className = classNames({
    'grouped-list__row-item': true,
    order: true,
    'order-row': isRow,
    'order-column border-marker--bold': isColumn,
    'border-marker--sell': action === OrderAction.SELL,
    'border-marker--buy': action === OrderAction.BUY,
  });
  const actionClassNames = classNames({
    order__action: true,
    N7_med_14: isRow,
    Body2_reg_14: isColumn,
    red_color_text: action === OrderAction.SELL,
    dark_green_color_text: action === OrderAction.BUY,
  });
  const currentPriceClassNames = classNames({
    'order__current-price': true,
    'order__current-price--red-text': Number(value) < Number(prevValue),
    'order__current-price--green-text': Number(value) > Number(prevValue),
  });
  const symbolClassNames = classNames({
    order__asset: true,
    Body2_reg_14: isColumn,
    H4_med14: isRow,
  });
  const typeClassNames = classNames({
    order__type: true,
    Body2_reg_14: isRow,
    Body5_extasmall_reg_10_lh16: isColumn,
  });
  const amountClassNames = classNames({
    order__amount: true,
    'order__amount--column': isColumn,
  });
  const amountValueClassNames = classNames({
    'order__amount-value': true,
    'order__amount-value--underlined': isOrderPartiallyCompleted,
  });
  const dateTimeClassNames = classNames({
    order__datetime: true,
    Body5_extasmall_reg_10_lh16: isColumn,
    Body4_small_reg_12: isRow,
  });
  const costClassNames = classNames({
    order__cost: true,
    Body4_small_reg_12: isRow,
    'order__cost--column': isColumn,
  });
  const priceClassNames = classNames({
    order__price: true,
    'order__price--column': isColumn,
  });

  const dateFormat = useMemo(() => (isColumn ? 'HH:mm' : 'MMM dd, yyyy HH:mm'), [isColumn]);

  const link = useMemo(
    () =>
      generatePath(routes.getSpotAssetPage(), {
        symbol: `${baseCurrency}-${quoteCurrency}`,
      }),
    [baseCurrency, quoteCurrency],
  );

  const { decimalPlaces: digitsForCost = 0 } = useWallet(quoteCurrency ?? '');

  const isSpotAvailable = useMemo(() => checkFeaturesAvailability('spot'), [checkFeaturesAvailability]);

  const executedAmount = useMemo(
    () =>
      localizeNumber(initialAmount - amount, {
        maxDigits: detectPrecision(amount),
      }),
    [amount, initialAmount],
  );
  const fullAmount = useMemo(
    () =>
      localizeNumber(initialAmount, {
        maxDigits: detectPrecision(initialAmount),
      }),
    [initialAmount],
  );

  const costValue = useMemo(
    () =>
      localizeNumber(calculateOrderCost(amount, price, action, makerCommission), {
        minDigits: digitsForCost,
        rounding: isBuy ? 'up' : 'down',
      }),
    [action, amount, digitsForCost, isBuy, makerCommission, price],
  );

  const amountTooltipTrigger: TooltipTriggerType = useMemo(() => (isMobile ? ['click'] : ['hover']), [isMobile]);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (disableRedirect || !isSpotAvailable) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [disableRedirect, isSpotAvailable],
  );

  const dropdownValues = useMemo<Array<{ value: DropdownActions; label: string }>>(
    () => [
      {
        value: 'edit',
        icon: svgIcons.Edit(),
        label: translate.toText('mobile:editlimitorder:dropdown-edit'),
      },
      {
        value: 'delete',
        icon: svgIcons.Delete(),
        label: translate.toText('mobile:editlimitorder:dropdown-delete'),
      },
    ],
    [translate],
  );

  const overlayContent = useMemo(
    () => (
      <div className='order-tooltip'>
        <div className='order-tooltip__row'>
          <div className='order-tooltip__title'>{translate.toText('mobile:order:amount:label')}</div>
          <div className='order-tooltip__value'>
            {localizeNumber(initialAmount, {
              maxDigits: detectPrecision(initialAmount),
            })}{' '}
            {baseCurrency}
          </div>
        </div>
        <span className='tooltip-divider' />
        <div className='order-tooltip__row'>
          <div className='order-tooltip__title'>{translate.toText('mobile:order:amount:completed:label')}</div>
          <div className='order-tooltip__value'>
            {executedAmount} {baseCurrency}
          </div>
        </div>
        <div className='order-tooltip__row'>
          <div className='order-tooltip__title'>{translate.toText('mobile:order:amount:pending:label')}</div>
          <div className='order-tooltip__value'>
            {localizeNumber(amount, { maxDigits: detectPrecision(amount) })} {baseCurrency}
          </div>
        </div>
      </div>
    ),
    [amount, baseCurrency, executedAmount, initialAmount, translate],
  );

  const orderAction = useMemo(
    () => (
      <div className='flex flex-align-center gap-8'>
        <div data-test='order-action' className={actionClassNames}>
          {capitalizeFirstLetter(translate.toText(`mobile:asset:order:${action}`).toLowerCase())}
        </div>
        {isRow && (
          <div data-test='order-type' className={typeClassNames}>
            {orderTypeText}
          </div>
        )}
      </div>
    ),
    [action, actionClassNames, isRow, orderTypeText, translate, typeClassNames],
  );

  const orderDatetime = useMemo(
    () => (
      <div data-test='order-datetime' className={dateTimeClassNames}>
        {formatDate(openTime, dateFormat)}
        {onCancel &&
          (isMobile ? (
            <Link
              className='order__edit-link'
              to={generatePath(routes.getEditLimitOrderPage(), {
                id: order.id,
                action: order.action,
              })}
            >
              <SvgIconUrl dataAttribute='order-edit' className='order__edit-icon' symbol={svgIcons.Edit()} />
            </Link>
          ) : (
            <Dropdown
              className='order__dropdown'
              overlayClassName='dropdown-edit'
              icon={svgIcons.VerticalMenu()}
              onSelect={onDropdownSelect}
              options={dropdownValues}
            />
          ))}
      </div>
    ),
    [
      dateFormat,
      dateTimeClassNames,
      dropdownValues,
      isMobile,
      onCancel,
      onDropdownSelect,
      openTime,
      order.action,
      order.id,
    ],
  );

  const orderAmount = useMemo(
    () => (
      <Tooltip
        overlay={overlayContent}
        placement='topLeft'
        enabled={isOrderPartiallyCompleted}
        trigger={amountTooltipTrigger}
      >
        <div className={amountClassNames}>
          {isColumn ? <span>{translate.toText('mobile:order:amount:label')}</span> : null}
          <span data-test='order-amount-value' className={amountValueClassNames}>
            {isOrderPartiallyCompleted ? (
              <NumbersConcealer data-test-prefix='order-amount'>
                {executedAmount}{' '}
                <span className='order__amount-value-total'>
                  {' '}
                  / {fullAmount} {isColumn && baseCurrency}
                </span>
              </NumbersConcealer>
            ) : (
              <NumbersConcealer data-test-prefix='order-amount'>
                {fullAmount} <span className='order__amount-value-total'>{baseCurrency}</span>
              </NumbersConcealer>
            )}
          </span>
          {isOrderPartiallyCompleted && <RangeCompletedIndicator initialAmount={initialAmount} amount={amount} />}
        </div>
      </Tooltip>
    ),
    [
      amount,
      amountClassNames,
      amountTooltipTrigger,
      amountValueClassNames,
      baseCurrency,
      executedAmount,
      fullAmount,
      initialAmount,
      isColumn,
      isOrderPartiallyCompleted,
      overlayContent,
      translate,
    ],
  );

  const orderCost = useMemo(
    () => (
      <div data-test='order-cost' className={costClassNames}>
        <div>{translate('mobile:order:amount:total:label')}</div>
        <div className='flex'>
          <div data-test='order-cost-value' className='order-column__right-value'>
            <NumbersConcealer data-test-prefix='order-cost'>≈ {costValue}</NumbersConcealer>
          </div>
          <div data-test='order-cost-currency' className='order-column__right-stat'>
            {quoteCurrency}
          </div>
        </div>
      </div>
    ),
    [costClassNames, costValue, quoteCurrency, translate],
  );

  const orderCostRow = useMemo(
    () => (
      <div data-test='order-cost' className='order__total'>
        <div data-test='order-cost-value' className='order__total-value'>
          <NumbersConcealer data-test-prefix='order-cost'>{costValue}</NumbersConcealer>
        </div>
        <div data-test='order-cost-currency'>{quoteCurrency}</div>
      </div>
    ),
    [costValue, quoteCurrency],
  );

  const orderCurrentPrice = useMemo(
    () => (
      <div data-test='order-current-price' className={currentPriceClassNames}>
        {isColumn && localizeNumber(value, { minDigits: digits })}
        <div className='order__current-price-icon'>
          {Number(value) > Number(prevValue) && <SvgIconUrl symbol={svgIcons.PriceChangeUp()} />}
          {Number(value) < Number(prevValue) && <SvgIconUrl symbol={svgIcons.PriceChangeDown()} />}
        </div>
        {isRow && localizeNumber(value, { minDigits: digits })}
        {isRow && <div className='order__current-price-currency'>{quoteCurrency}</div>}
      </div>
    ),
    [currentPriceClassNames, digits, isColumn, isRow, prevValue, quoteCurrency, value],
  );

  const orderPrice = useMemo(
    () => (
      <div className={priceClassNames}>
        {isColumn ? <span>{translate.toText('mobile:order:price:label')}</span> : null}
        <span data-test='order-price-value' className='order__price-value'>
          {localizeNumber(price ?? 0, { minDigits: digits })}
          {isColumn ? (
            <>
              <span className='order__price-value-divider'>/</span>
              {orderCurrentPrice}
            </>
          ) : null}
        </span>
        {!isColumn ? <div>{quoteCurrency}</div> : null}
      </div>
    ),
    [digits, isColumn, orderCurrentPrice, price, priceClassNames, quoteCurrency, translate],
  );

  const orderType = useMemo(
    () => (
      <div data-test='order-type' className={typeClassNames}>
        {orderTypeText}
      </div>
    ),
    [orderTypeText, typeClassNames],
  );

  const orderAsset = useMemo(
    () => (
      <div data-test='order-asset' className={symbolClassNames}>
        <AssetIcons name={`${baseCurrency}/${quoteCurrency}`} />
        <div className='order__asset-value'>{`${baseCurrency}/${quoteCurrency}`}</div>
      </div>
    ),
    [baseCurrency, quoteCurrency, symbolClassNames],
  );

  const openOrdersCard = useMemo(
    () => (
      <Link to={link} id={`order-${id}`} data-test='order' className={className} onClick={onClick}>
        <div className='order-column__left'>
          {orderAction}
          {orderType}
        </div>
        <div className='order-column__right'>
          <div className='order-column__row'>
            {orderAsset}
            {orderDatetime}
          </div>
          {orderAmount}
          {orderCost}
          {orderPrice}
        </div>
      </Link>
    ),
    [
      link,
      id,
      className,
      onClick,
      orderAction,
      orderType,
      orderAsset,
      orderDatetime,
      orderAmount,
      orderCost,
      orderPrice,
    ],
  );

  const openOrdersRow = useMemo(
    () => (
      <Link to={link} id={`order-${id}`} data-test='order' data-id={id} className={className} onClick={onClick}>
        {orderAction}
        {orderAmount}
        {orderCostRow}
        {orderPrice}
        {orderCurrentPrice}
        {orderDatetime}
      </Link>
    ),
    [
      className,
      id,
      link,
      onClick,
      orderAction,
      orderAmount,
      orderCostRow,
      orderCurrentPrice,
      orderDatetime,
      orderPrice,
    ],
  );

  return isRow ? openOrdersRow : openOrdersCard;
});
